import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import {
  CheckCircleIcon,
  XCircleIcon,
  FileIcon,
  AlertTriangleIcon,
  DownloadIcon,
  AlertCircleIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  RefreshCwIcon,
  InfoIcon,
  LightbulbIcon,
  FileTextIcon,
} from 'lucide-react';
import { getImportDetails, ImportTask } from '@/services/importService';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { ROUTES } from '@/config/routes';
import {
  useInvalidateAttributesCache,
  useInvalidateAttributeGroupsCache,
  useInvalidateAttributesOnlyCache,
  useInvalidateFamiliesCache,
} from '@/hooks/useGlobalAttributes';
import { useQueryClient } from '@tanstack/react-query';
import { createLogger } from '@/lib/logger';
import { errorReporting, captureImportError, captureNetworkError } from '@/lib/errorReporting';
import { useImportTranslations } from '@/hooks/useImportTranslations';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import BulkImageErrorDisplay from './components/BulkImageErrorDisplay';

interface StepProgressProps {
  importId: number;
  onComplete?: () => void;
  importType?: 'products' | 'families' | 'attributes' | 'attribute-groups' | 'bulk_images';
}

interface ErrorRow {
  row: string;
  sku: string;
  field: string;
  error: string;
}

interface ErrorCategory {
  type: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  errors: ErrorRow[];
  solutions: string[];
}

const POLLING_INTERVAL = 500; // 500ms for faster polling
const PROGRESS_SIMULATION_INTERVAL = 100; // Update progress every 100ms
const MAX_ERROR_ROWS = 10; // Maximum number of error rows to display

// Create component-specific logger
const stepProgressLogger = createLogger('StepProgress');

// Enhanced error categorization with solutions
const categorizeErrors = (errors: ErrorRow[], t: any): ErrorCategory[] => {
  const categories: Record<string, ErrorCategory> = {};

  errors.forEach(error => {
    let categoryKey = 'unknown';
    let category: Partial<ErrorCategory> = {
      type: 'unknown',
      title: t('imports.errorHandling.categoryError.unknown.title'),
      description: t('imports.errorHandling.categoryError.unknown.description'),
      icon: AlertTriangleIcon,
      color: 'gray',
      solutions: t('imports.errorHandling.categoryError.unknown.solutions', {
        returnObjects: true,
      }),
    };

    // Bulk image specific errors
    if (
      error.error.includes('dimensions too large') ||
      (error.error.includes('maximum') && error.error.includes('pixels'))
    ) {
      categoryKey = 'imageDimensions';
      category = {
        type: 'imageDimensions',
        title: t('imports.errorHandling.categoryError.imageDimensions.title'),
        description: t('imports.errorHandling.categoryError.imageDimensions.description'),
        icon: AlertTriangleIcon,
        color: 'orange',
        solutions: t('imports.errorHandling.categoryError.imageDimensions.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Image file size errors
    else if (
      error.error.includes('too large') &&
      (error.error.includes('MB') || error.error.includes('size'))
    ) {
      categoryKey = 'imageFileSize';
      category = {
        type: 'imageFileSize',
        title: t('imports.errorHandling.categoryError.imageFileSize.title'),
        description: t('imports.errorHandling.categoryError.imageFileSize.description'),
        icon: AlertTriangleIcon,
        color: 'red',
        solutions: t('imports.errorHandling.categoryError.imageFileSize.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Image format/type errors
    else if (
      error.error.includes('invalid type') ||
      (error.error.includes('format') && error.error.includes('image'))
    ) {
      categoryKey = 'imageFormat';
      category = {
        type: 'imageFormat',
        title: t('imports.errorHandling.categoryError.imageFormat.title'),
        description: t('imports.errorHandling.categoryError.imageFormat.description'),
        icon: XCircleIcon,
        color: 'red',
        solutions: t('imports.errorHandling.categoryError.imageFormat.solutions', {
          returnObjects: true,
        }),
      };
    }
    // SKU/product matching errors for bulk images
    else if (error.error.includes('No matching product') || error.error.includes('product found')) {
      categoryKey = 'productMatching';
      category = {
        type: 'productMatching',
        title: t('imports.errorHandling.categoryError.productMatching.title'),
        description: t('imports.errorHandling.categoryError.productMatching.description'),
        icon: FileIcon,
        color: 'blue',
        solutions: t('imports.errorHandling.categoryError.productMatching.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Corrupted image files
    else if (
      error.error.includes('corrupted') ||
      error.error.includes('invalid image') ||
      error.error.includes('cannot open')
    ) {
      categoryKey = 'corruptedImages';
      category = {
        type: 'corruptedImages',
        title: t('imports.errorHandling.categoryError.corruptedImages.title'),
        description: t('imports.errorHandling.categoryError.corruptedImages.description'),
        icon: XCircleIcon,
        color: 'red',
        solutions: t('imports.errorHandling.categoryError.corruptedImages.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Measurement attribute errors
    else if (
      error.error.includes('default unit') ||
      error.error.includes('unit') ||
      error.error.includes('measurement')
    ) {
      categoryKey = 'measurement';
      category = {
        type: 'measurement',
        title: t('imports.errorHandling.categoryError.measurement.title'),
        description: t('imports.errorHandling.categoryError.measurement.description'),
        icon: AlertTriangleIcon,
        color: 'orange',
        solutions: t('imports.errorHandling.categoryError.measurement.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Select attribute errors
    else if (
      error.error.includes('option') ||
      error.error.includes('select') ||
      error.error.includes('choice')
    ) {
      categoryKey = 'select';
      category = {
        type: 'select',
        title: t('imports.errorHandling.categoryError.select.title'),
        description: t('imports.errorHandling.categoryError.select.description'),
        icon: AlertCircleIcon,
        color: 'blue',
        solutions: t('imports.errorHandling.categoryError.select.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Family/attribute relationship errors
    else if (error.error.includes('family') || error.error.includes('not in family')) {
      categoryKey = 'family';
      category = {
        type: 'family',
        title: t('imports.errorHandling.categoryError.family.title'),
        description: t('imports.errorHandling.categoryError.family.description'),
        icon: FileTextIcon,
        color: 'purple',
        solutions: t('imports.errorHandling.categoryError.family.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Missing reference errors
    else if (error.error.includes('not found') || error.error.includes('missing')) {
      categoryKey = 'references';
      category = {
        type: 'references',
        title: t('imports.errorHandling.categoryError.references.title'),
        description: t('imports.errorHandling.categoryError.references.description'),
        icon: XCircleIcon,
        color: 'red',
        solutions: t('imports.errorHandling.categoryError.references.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Validation errors
    else if (
      error.error.includes('validation') ||
      error.error.includes('format') ||
      error.error.includes('invalid')
    ) {
      categoryKey = 'validation';
      category = {
        type: 'validation',
        title: t('imports.errorHandling.categoryError.validation.title'),
        description: t('imports.errorHandling.categoryError.validation.description'),
        icon: AlertTriangleIcon,
        color: 'yellow',
        solutions: t('imports.errorHandling.categoryError.validation.solutions', {
          returnObjects: true,
        }),
      };
    }
    // Duplicate errors
    else if (error.error.includes('duplicate') || error.error.includes('already exists')) {
      categoryKey = 'duplicates';
      category = {
        type: 'duplicates',
        title: t('imports.errorHandling.categoryError.duplicates.title'),
        description: t('imports.errorHandling.categoryError.duplicates.description'),
        icon: AlertCircleIcon,
        color: 'indigo',
        solutions: t('imports.errorHandling.categoryError.duplicates.solutions', {
          returnObjects: true,
        }),
      };
    }

    if (!categories[categoryKey]) {
      categories[categoryKey] = {
        ...category,
        errors: [],
      } as ErrorCategory;
    }

    categories[categoryKey].errors.push(error);
  });

  return Object.values(categories);
};

// Additional metrics for bulk image imports
const getBulkImageMetrics = (task: ImportTask | null) => {
  if (!task || task.import_type !== 'bulk_images') return null;

  return {
    totalFiles: task.uploaded_files_count || 0,
    matchedProducts: task.matched_products_count || 0,
    imagesCreated: task.images_created_count || 0,
    unmatchedFiles: (task.uploaded_files_count || 0) - (task.images_created_count || 0),
  };
};

// Convert ErrorRow to BulkImageError for enhanced display
const convertToBulkImageErrors = (errorRows: ErrorRow[], t: any) => {
  return errorRows.map(row => {
    const error = row.error.toLowerCase();
    let type: 'dimensions' | 'fileSize' | 'format' | 'corrupted' | 'noProduct' | 'general' =
      'general';

    if (error.includes('dimensions') || error.includes('pixels')) {
      type = 'dimensions';
    } else if (error.includes('too large') || error.includes('size')) {
      type = 'fileSize';
    } else if (error.includes('invalid type') || error.includes('format')) {
      type = 'format';
    } else if (error.includes('corrupted') || error.includes('invalid image')) {
      type = 'corrupted';
    } else if (error.includes('no matching product') || error.includes('product found')) {
      type = 'noProduct';
    }

    return {
      filename: row.sku || t('imports.errorHandling.unknownFile'), // For bulk images, filename might be in SKU field
      sku: row.sku,
      error: row.error,
      type,
    };
  });
};

const StepProgress: React.FC<StepProgressProps> = ({ importId, onComplete, importType }) => {
  const [importTask, setImportTask] = useState<ImportTask | null>(null);
  const [progress, setProgress] = useState(0);
  const [realProgress, setRealProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorRows, setErrorRows] = useState<ErrorRow[]>([]);
  const [isLoadingErrors, setIsLoadingErrors] = useState(false);
  const [isSimulating, setIsSimulating] = useState(true);
  const [shouldStopPolling, setShouldStopPolling] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidateAttributesCache = useInvalidateAttributesCache();
  const invalidateAttributeGroupsCache = useInvalidateAttributeGroupsCache();
  const invalidateAttributesOnlyCache = useInvalidateAttributesOnlyCache();
  const invalidateFamiliesCache = useInvalidateFamiliesCache();
  const queryClient = useQueryClient();

  // Initialize translations
  const { getProgressText, getButtonText, getSuccessMessage, getErrorMessage, t } =
    useImportTranslations();

  // Use ref to store the interval so we can reliably clear it
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Progress simulation effect
  useEffect(() => {
    if (!isSimulating) return;

    const simulationInterval = setInterval(() => {
      setProgress(prev => {
        // If we have real progress, stop simulating
        if (realProgress !== null) {
          setIsSimulating(false);
          return realProgress;
        }

        // Simulate smooth progress with diminishing increments
        // Fast growth early, slower as it approaches higher values
        if (prev < 20) return prev + 1;
        if (prev < 40) return prev + 0.8;
        if (prev < 60) return prev + 0.6;
        if (prev < 80) return prev + 0.4;
        if (prev < 90) return prev + 0.2;
        return prev; // Stop at 90% until real data arrives
      });
    }, PROGRESS_SIMULATION_INTERVAL);

    return () => clearInterval(simulationInterval);
  }, [isSimulating, realProgress]);

  // Real progress polling effect
  useEffect(() => {
    if (shouldStopPolling) {
      stepProgressLogger.debug('Polling stopped by flag', { importId });
      return;
    }

    const fetchStatus = async () => {
      if (shouldStopPolling) {
        stepProgressLogger.debug('Fetch cancelled - polling stopped', {
          importId,
        });
        return;
      }

      try {
        const response = await getImportDetails(importId);
        const task = response.data;
        setImportTask(task);

        // Calculate real progress
        if (task.total_rows && task.total_rows > 0) {
          const percentage = Math.min(
            100,
            Math.round(((task.processed || 0) / task.total_rows) * 100)
          );
          setRealProgress(percentage);

          // Gradually update progress towards real progress instead of jumping instantly
          setProgress(prev => {
            const diff = percentage - prev;
            if (Math.abs(diff) <= 1) {
              setIsSimulating(false);
              return percentage;
            }
            // Move gradually towards the target (20% of the way each update for smooth animation)
            return Math.round(prev + diff * 0.2);
          });
        }

        // Check if import has finished
        if (task.status !== 'queued' && task.status !== 'running') {
          // Stop polling immediately
          setShouldStopPolling(true);

          // Stop simulation and set final progress
          setIsSimulating(false);
          setRealProgress(100);
          setProgress(100);

          // Show success or error message
          if (task.status === 'success') {
            toast({
              title: getSuccessMessage('importCompleted'),
              description: t('imports.success.importCompletedDesc', {
                count: task.processed || 0,
              }),
            });

            // Call onComplete callback if provided
            if (onComplete) {
              onComplete();
            }

            // Invalidate caches based on import type
            if (importType === 'families') {
              invalidateFamiliesCache();
            } else if (importType === 'attributes') {
              invalidateAttributesOnlyCache();
            } else if (importType === 'attribute-groups') {
              invalidateAttributeGroupsCache();
            } else if (importType === 'products') {
              // For product imports, invalidate product-related caches
              queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['product'] });
              queryClient.invalidateQueries({ queryKey: ['product-detail'] });
            }

            // Always invalidate comprehensive cache for structure imports
            if (
              importType === 'families' ||
              importType === 'attributes' ||
              importType === 'attribute-groups'
            ) {
              invalidateAttributesCache();
            }
          } else if (task.status === 'error' || task.status === 'partial_success') {
            setError(
              task.status === 'error'
                ? getErrorMessage('import.processingFailed')
                : getErrorMessage('importCompletedWithErrors')
            );

            toast({
              title: getErrorMessage('importCompletedWithErrors'),
              description: task.error_count
                ? t('imports.errors.errorCount', { count: task.error_count })
                : getErrorMessage('someRowsNotImported'),
              variant: 'destructive',
            });
          }
        }
      } catch (error) {
        captureNetworkError(error as Error, {
          url: `/api/imports/${importId}`,
          method: 'GET',
          component: 'StepProgress',
        });
        stepProgressLogger.error(
          'Error fetching import status',
          {
            importId,
            error: (error as Error).message,
          },
          error as Error
        );

        setError(getErrorMessage('statusFetchFailed'));
        setShouldStopPolling(true);
      }
    };

    // Initial fetch
    fetchStatus();

    // Set up polling
    pollingIntervalRef.current = setInterval(fetchStatus, POLLING_INTERVAL);

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    importId,
    shouldStopPolling,
    onComplete,
    importType,
    invalidateAttributesCache,
    invalidateAttributeGroupsCache,
    invalidateAttributesOnlyCache,
    invalidateFamiliesCache,
    queryClient,
    toast,
    getSuccessMessage,
    getErrorMessage,
    t,
  ]);

  // Fetch error details when error_file_url is available
  useEffect(() => {
    if (
      importTask?.error_file_url &&
      importTask.error_count &&
      importTask.error_count > 0 &&
      errorRows.length === 0
    ) {
      setIsLoadingErrors(true);

      axios
        .get(importTask.error_file_url)
        .then(response => {
          // Parse CSV data
          const csvText = response.data;
          const rows = csvText.split('\n');

          if (rows.length > 1) {
            // At least header + one row
            const parsedRows: ErrorRow[] = [];

            // Skip header row (row 0) and parse up to MAX_ERROR_ROWS
            for (let i = 1; i < Math.min(rows.length, MAX_ERROR_ROWS + 1); i++) {
              if (rows[i].trim()) {
                const columns = rows[i].split(',');
                if (columns.length >= 4) {
                  parsedRows.push({
                    row: columns[0],
                    sku: columns[1],
                    field: columns[2],
                    error: columns[3],
                  });
                }
              }
            }

            setErrorRows(parsedRows);
          }
        })
        .catch(err => {
          captureNetworkError(err, {
            url: importTask.error_file_url,
            method: 'GET',
            component: 'StepProgress',
          });
          stepProgressLogger.error(
            'Failed to fetch error details',
            {
              importId: importTask.id,
              errorFileUrl: importTask.error_file_url,
              error: err.message,
            },
            err
          );
        })
        .finally(() => {
          setIsLoadingErrors(false);
        });
    }
  }, [importTask]);

  const handleViewProducts = () => {
    navigate('/app/products');
  };

  const handleDownloadErrors = () => {
    if (importTask?.error_file_url) {
      window.open(importTask.error_file_url, '_blank');
    }
  };

  const toggleCategoryExpansion = (categoryType: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryType)) {
        newSet.delete(categoryType);
      } else {
        newSet.add(categoryType);
      }
      return newSet;
    });
  };

  const renderErrorBadge = () => {
    // Stricter condition to ensure we never render when error_count is 0 or undefined
    if (!importTask?.error_count || importTask.error_count <= 0) {
      return null;
    }

    return (
      <Badge variant="destructive" className="ml-2" data-testid="import-error-badge">
        {importTask.error_count}{' '}
        {importTask.error_count === 1
          ? t('imports.errors.badge.error')
          : t('imports.errors.badge.errors')}
      </Badge>
    );
  };

  // Categorize errors
  const errorCategories = useMemo(() => {
    return categorizeErrors(errorRows, t);
  }, [errorRows, t]);

  const renderBulkImageMetrics = () => {
    const metrics = getBulkImageMetrics(importTask);
    if (!metrics) return null;

    return (
      <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="bulk-image-metrics">
        <div className="rounded-lg bg-blue-50 p-3 text-center">
          <div className="text-2xl font-bold text-blue-600">{metrics.totalFiles}</div>
          <div className="text-sm text-blue-600">{getProgressText('totalFiles')}</div>
        </div>
        <div className="rounded-lg bg-green-50 p-3 text-center">
          <div className="text-2xl font-bold text-green-600">{metrics.imagesCreated}</div>
          <div className="text-sm text-green-600">{getProgressText('imagesCreated')}</div>
        </div>
        <div className="rounded-lg bg-purple-50 p-3 text-center">
          <div className="text-2xl font-bold text-purple-600">{metrics.matchedProducts}</div>
          <div className="text-sm text-purple-600">{getProgressText('matchedProducts')}</div>
        </div>
        {metrics.unmatchedFiles > 0 && (
          <div className="rounded-lg bg-orange-50 p-3 text-center">
            <div className="text-2xl font-bold text-orange-600">{metrics.unmatchedFiles}</div>
            <div className="text-sm text-orange-600">{getProgressText('unmatchedFiles')}</div>
          </div>
        )}
      </div>
    );
  };

  const renderInlineErrors = () => {
    if (errorCategories.length === 0) return null;

    // Use enhanced bulk image error display for bulk image imports
    if (importType === 'bulk_images' && errorRows.length > 0) {
      const bulkImageErrors = convertToBulkImageErrors(errorRows, t);
      const totalFiles = importTask?.uploaded_files_count || bulkImageErrors.length;

      return (
        <BulkImageErrorDisplay
          errors={bulkImageErrors}
          totalFiles={totalFiles}
          onRetry={() => window.location.reload()} // Simple retry for now
          onDownloadReport={importTask?.error_file_url ? handleDownloadErrors : undefined}
        />
      );
    }

    // Default error display for other import types
    return (
      <div className="mt-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center text-lg font-semibold">
            <AlertTriangleIcon className="mr-2 size-5 text-red-500" />
            {t('imports.errorHandling.importErrorsFound')}
          </h3>
          {importTask?.error_file_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadErrors}
              className="flex items-center gap-2"
            >
              <DownloadIcon className="size-4" />
              {t('imports.errorHandling.downloadFullErrorReport')}
            </Button>
          )}
        </div>

        {errorCategories.map(category => (
          <Card
            key={category.type}
            className="border-l-4"
            style={{ borderLeftColor: getCategoryColor(category.color) }}
          >
            <CardHeader className="pb-3">
              <Collapsible>
                <CollapsibleTrigger
                  className="flex w-full items-center justify-between text-left"
                  onClick={() => toggleCategoryExpansion(category.type)}
                >
                  <div className="flex items-center space-x-3">
                    <category.icon className={`text- size-5${category.color}-500`} />
                    <div>
                      <CardTitle className="text-base">{category.title}</CardTitle>
                      <CardDescription className="text-sm">{category.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary">
                      {category.errors.length}{' '}
                      {category.errors.length === 1
                        ? t('imports.errors.badge.error')
                        : t('imports.errors.badge.errors')}
                    </Badge>
                    {expandedCategories.has(category.type) ? (
                      <ChevronUpIcon className="size-4" />
                    ) : (
                      <ChevronDownIcon className="size-4" />
                    )}
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent className="mt-4">
                  <CardContent className="pt-0">
                    {/* Solutions */}
                    <Alert className="mb-4">
                      <LightbulbIcon className="size-4" />
                      <AlertTitle>{t('imports.errorHandling.howToFixErrors')}</AlertTitle>
                      <AlertDescription>
                        <ul className="list-inside list-disc space-y-1 text-sm">
                          {category.solutions.map((solution, index) => (
                            <li key={index}>{solution}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>

                    {/* Error Details */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">
                        {t('imports.errorHandling.errorDetails')}
                      </h4>
                      <div className="max-h-48 space-y-2 overflow-y-auto">
                        {category.errors.slice(0, 5).map((error, index) => (
                          <div key={index} className="rounded bg-gray-50 p-3 text-sm">
                            <div className="flex items-start justify-between">
                              <div className="space-y-1">
                                <div className="font-medium">
                                  {t('imports.errorHandling.row')} {error.row} •{' '}
                                  {t('imports.errorHandling.sku')} {error.sku}
                                </div>
                                <div className="text-red-600">
                                  {t('imports.errorHandling.field')} {error.field}
                                </div>
                                <div className="text-gray-700">{error.error}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {category.errors.length > 5 && (
                          <div className="py-2 text-center text-sm text-muted-foreground">
                            {t('imports.errorHandling.moreErrorsOfType', {
                              count: category.errors.length - 5,
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </CardHeader>
          </Card>
        ))}
      </div>
    );
  };

  const getCategoryColor = (color: string) => {
    const colorMap: Record<string, string> = {
      red: '#ef4444',
      orange: '#f97316',
      yellow: '#eab308',
      blue: '#3b82f6',
      purple: '#8b5cf6',
      indigo: '#6366f1',
      gray: '#6b7280',
    };
    return colorMap[color] || colorMap.gray;
  };

  const getStatusIcon = () => {
    if (error) {
      return <XCircleIcon className="size-6 text-red-500" />;
    }

    if (!importTask) {
      return <RefreshCwIcon className="size-6 animate-spin text-blue-500" />;
    }

    switch (importTask.status) {
      case 'success':
        return <CheckCircleIcon className="size-6 text-green-500" />;
      case 'error':
        return <XCircleIcon className="size-6 text-red-500" />;
      case 'partial_success':
        return <AlertCircleIcon className="size-6 text-yellow-500" />;
      case 'running':
      case 'queued':
        return <RefreshCwIcon className="size-6 animate-spin text-blue-500" />;
      default:
        return <InfoIcon className="size-6 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    if (error) {
      return getProgressText('error');
    }

    if (!importTask) {
      return getProgressText('initializing');
    }

    switch (importTask.status) {
      case 'success':
        return getProgressText('completed');
      case 'error':
        return getProgressText('failed');
      case 'partial_success':
        return getProgressText('partialSuccess');
      case 'running':
        return getProgressText('processing');
      case 'queued':
        return getProgressText('queued');
      default:
        return getProgressText('unknown');
    }
  };

  const isCompleted = importTask?.status === 'success' || importTask?.status === 'partial_success';

  return (
    <div className="space-y-6" data-testid="step-progress-container">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            {getStatusIcon()}
            <span className="ml-2">{getProgressText('title')}</span>
            {renderErrorBadge()}
          </CardTitle>
          <CardDescription>{getStatusText()}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{getProgressText('progress')}</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>

          {importTask && (
            <div className="grid grid-cols-2 gap-4 text-sm" data-testid="import-stats">
              <div>
                <span className="font-medium">{getProgressText('totalRows')}: </span>
                <span data-testid="total-rows-value">{importTask.total_rows || 0}</span>
              </div>
              <div>
                <span className="font-medium">{getProgressText('processed')}: </span>
                <span data-testid="processed-value">{importTask.processed || 0}</span>
              </div>
            </div>
          )}

          {renderBulkImageMetrics()}

          {error && (
            <Alert variant="destructive">
              <AlertCircleIcon className="size-4" />
              <AlertTitle>{getProgressText('error')}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isCompleted && (
            <div className="flex gap-2 pt-4">
              <Button onClick={handleViewProducts}>
                <EyeIcon className="mr-2 size-4" />
                {getButtonText('viewProducts')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Inline Error Display */}
      {renderInlineErrors()}
    </div>
  );
};

export default StepProgress;
